/**
 * Writer Page
 *
 * Streams transcript text in real-time to connected viewers via WebSocket.
 * Requires authentication. Uses SessionManager to create/join sessions.
 */

import { resolveApiBaseUrl, writerLinesFromDocument } from '@polygraf/shared'
import {
	ArrowLeft,
	Check,
	ChevronDown,
	Copy,
	LogIn,
	QrCode,
	RefreshCw,
	Shield,
	Square,
	Trash2,
	Users,
	Wifi,
	WifiOff,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthGuard } from '../components/auth-guard'
import { SessionManager } from '../components/session-manager'
import { Alert, Button, IconButton, Spinner, buttonClasses, cn } from '../components/ui'
import { useAuthToken } from '../hooks/use-auth-token'
import { usePageTitle } from '../hooks/use-page-title'
import { useUserRole } from '../hooks/use-user-role'
import { useWriterSocket } from '../hooks/use-writer-socket'
import { trpc } from '../trpc/client'

function getSessionViewerUrl(documentId: string): string {
	return `${window.location.origin}/view/session/${documentId}`
}

function WriterContent() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { isPresenter, isLoading: roleLoading } = useUserRole()
	const [searchParams] = useSearchParams()

	// When a roomId is present, gate on the room's server-computed `canPresent`
	// — the same predicate `liveSessions.ensure` enforces (global PRESENTER+,
	// space owner, space/room EDITOR+ member). This page previously checked
	// `canManage` only, so a space EDITOR saw the "Spustit testovací webový
	// přepis" button on the room tile (which keys off canPresent, MU #3) and
	// then hit the "Vyžaduje se role Přednášející" wall here even though the
	// server would have authorized the session (found 2026-08-04 while
	// role-testing as a space editor). Global presenters always pass.
	const writerRoomId = searchParams.get('roomId')?.trim() || undefined
	// The room service this transcript streams for (set by the room tile's
	// "Spustit testovací webový přepis"); absent for SessionManager sessions.
	const writerServiceId = searchParams.get('serviceId')?.trim() || undefined
	const { data: writerRoom, isLoading: roomLoading } = trpc.rooms.get.useQuery(
		{ id: writerRoomId ?? '' },
		{ enabled: !!writerRoomId },
	)
	const canWrite = isPresenter || writerRoom?.canPresent === true

	usePageTitle(
		!roleLoading && !canWrite ? t('sessions.create.presenterRequiredTitle') : t('nav.writer'),
	)
	const token = useAuthToken()

	const [documentId, setDocumentId] = useState('')
	const [sessionTitle, setSessionTitle] = useState('')
	const [isSessionActive, setIsSessionActive] = useState(false)
	const [showQr, setShowQr] = useState(false)
	const [showViewers, setShowViewers] = useState(false)
	const [copied, setCopied] = useState(false)

	// Text editing state
	const [lines, setLines] = useState<Array<{ id: string; text: string }>>([])
	const [currentLineId, setCurrentLineId] = useState<string | null>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const activeDocRef = useRef<string>('')
	/** Document whose server text was already put back into the editor. */
	const hydratedDocRef = useRef('')

	const leaveMutation = trpc.liveSessions.leave.useMutation({
		onError: (err) => {
			console.error('Failed to leave session:', err.message)
		},
	})

	const {
		isConnected,
		isConnecting,
		isRegistered,
		error,
		errorKind,
		subscriberCount,
		document: writerDocument,
		reconnect,
		insertParagraph,
		replaceParagraph,
		deleteParagraphs,
		cancelPendingStream,
		streamText,
	} = useWriterSocket({
		documentId: isSessionActive ? documentId : '',
		title: isSessionActive ? sessionTitle : '',
		token,
	})

	// MP-194: the transcriber's live viewer ROSTER (who is reading), keyed by the
	// documentId the Writer holds (sessions.viewersByDocument resolves the unique
	// documentId → session). Polled only while the panel is open, like the
	// presenter page, to avoid extra LiveKit calls when collapsed.
	const viewersQuery = trpc.sessions.viewersByDocument.useQuery(
		{ documentId },
		{
			enabled: isSessionActive && isRegistered && showViewers && !!documentId,
			refetchInterval: 15_000,
		},
	)

	// Terminal failures (expired auth session, exhausted automatic retries) get
	// a recovery panel with explicit actions instead of a bare red status.
	const isAuthError = errorKind === 'auth'
	const isTerminalFailure = isAuthError || errorKind === 'exhausted'

	const sessionViewerUrl = documentId ? getSessionViewerUrl(documentId) : ''

	const handleSessionReady = useCallback((docId: string, title: string) => {
		setDocumentId(docId)
		setSessionTitle(title)
		setLines([])
		setCurrentLineId(null)
		setIsSessionActive(true)
		activeDocRef.current = docId
		hydratedDocRef.current = ''
	}, [])

	// Ensure DB session exists (re-creates if deleted by HMR or page reload)
	const ensureMutation = trpc.liveSessions.ensure.useMutation({
		onError: (err) => {
			console.error('Failed to ensure session in DB:', err.message)
		},
	})

	// Auto-start session from URL params (e.g. redirected from room page)
	const autoStartedRef = useRef(false)
	useEffect(() => {
		const paramDocId = searchParams.get('documentId')
		const paramTitle = searchParams.get('title')
		if (paramDocId && paramTitle) {
			if (!autoStartedRef.current) {
				autoStartedRef.current = true
				handleSessionReady(paramDocId, paramTitle)
			}
			// Always ensure DB record exists (covers HMR re-mounts and reloads —
			// the WS server deletes the row when the writer socket closes). The
			// service link must travel with it, or the re-created row is
			// invisible to the room tile's liveness (MU 2026-09-04, N1/N2).
			const paramRoomId = searchParams.get('roomId') ?? undefined
			ensureMutation.mutate({
				documentId: paramDocId,
				title: paramTitle,
				role: 'WRITER',
				roomId: paramRoomId,
				serviceId: writerServiceId,
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Re-login route for an expired auth session. The returnTo URL carries
	// documentId/title/roomId explicitly (sessions started via SessionManager
	// don't have them in the current URL) so the auto-start effect above
	// re-attaches to the SAME document after login — the server keeps the
	// document state, so viewers keep the transcript streamed so far, and the
	// editor is refilled from it once the writer registers again.
	const handleLogInAgain = useCallback(() => {
		const params = new URLSearchParams({ documentId, title: sessionTitle })
		if (writerRoomId) params.set('roomId', writerRoomId)
		if (writerServiceId) params.set('serviceId', writerServiceId)
		navigate(`/auth?returnTo=${encodeURIComponent(`/writer?${params.toString()}`)}`)
	}, [navigate, documentId, sessionTitle, writerRoomId, writerServiceId])

	const handleStopSession = useCallback(() => {
		if (documentId) {
			leaveMutation.mutate({ documentId, role: 'WRITER' })
		}
		setIsSessionActive(false)
		setDocumentId('')
		setSessionTitle('')
		setLines([])
		setCurrentLineId(null)
		activeDocRef.current = ''
		hydratedDocRef.current = ''
	}, [documentId, leaveMutation])

	// Cleanup: leave session on tab close OR when the user navigates away
	// inside the SPA. The effect cleanup fires both during HMR and on real
	// unmount; we skip it under HMR (`import.meta.hot` is dev-only) so a hot
	// reload doesn't kill the live DB record.
	useEffect(() => {
		const fireLeave = () => {
			if (!activeDocRef.current) return
			// httpBatchLink-compatible: same host as the rest of trpc
			// (api.polygraf.app in prod, relative for the Vite proxy in
			// dev) AND batched indexed body shape so the server's
			// /api/trpc/<proc>?batch=1 route accepts it. Without this
			// the request 405'd on polygraf.app (the SPA host has no
			// /api/trpc route).
			const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL)
			const url = `${API_URL}/api/trpc/liveSessions.leave?batch=1`
			const body = JSON.stringify({
				0: { json: { documentId: activeDocRef.current, role: 'WRITER' } },
			})
			// Prefer sendBeacon — guaranteed to flush during unload.
			// Falls back to fetch+keepalive when it returns false
			// (some browsers refuse sendBeacon with cross-origin
			// credentialed JSON).
			const blob = new Blob([body], { type: 'application/json' })
			const sent = navigator.sendBeacon?.(url, blob)
			if (!sent) {
				fetch(url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body,
					credentials: 'include',
					keepalive: true,
				}).catch(() => {})
			}
		}
		window.addEventListener('beforeunload', fireLeave)
		return () => {
			window.removeEventListener('beforeunload', fireLeave)
			// Only fire on real production unmounts. In dev, HMR causes
			// spurious unmount/remount cycles that would leak `leave` calls.
			if (!import.meta.hot) {
				fireLeave()
			}
		}
	}, [])

	const handleTextareaChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			if (!isRegistered) return

			const text = e.target.value
			const textLines = text.split('\n')

			const newLines: Array<{ id: string; text: string }> = []

			for (let i = 0; i < textLines.length; i++) {
				const existingLine = lines[i]
				if (existingLine) {
					newLines.push({ id: existingLine.id, text: textLines[i] })
				} else {
					const id = `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
					newLines.push({ id, text: textLines[i] })
				}
			}

			for (let i = 0; i < newLines.length; i++) {
				const oldLine = lines[i]
				const newLine = newLines[i]

				if (!oldLine) {
					insertParagraph(newLine.text, newLine.id)
					streamText(newLine.id, newLine.text, true)
				} else if (oldLine.text !== newLine.text) {
					replaceParagraph(newLine.id, [{ text: newLine.text }])
					streamText(newLine.id, newLine.text, false)
				}
			}

			// Lines that no longer exist must be deleted on the server too. Only
			// the new lines were walked above, so removing several lines (or
			// joining two) left their paragraphs with every viewer, and Clear —
			// which only knew the current lines — could not remove them either
			// (retest 24. 9. 2026, A2). One message, so the server cannot race.
			const removedIds = lines.slice(newLines.length).map((line) => line.id)
			if (removedIds.length > 0) {
				cancelPendingStream()
				deleteParagraphs(removedIds)
			}

			setLines(newLines)
			setCurrentLineId(newLines[newLines.length - 1]?.id ?? null)
		},
		[
			isRegistered,
			lines,
			insertParagraph,
			replaceParagraph,
			deleteParagraphs,
			cancelPendingStream,
			streamText,
		],
	)

	const handleCopyUrl = useCallback(async () => {
		if (!sessionViewerUrl) return
		try {
			await navigator.clipboard.writeText(sessionViewerUrl)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			// Clipboard API may not be available
		}
	}, [sessionViewerUrl])

	const handleClearText = useCallback(() => {
		// MP-178: "Clear" must propagate to viewers, not just wipe the local
		// textarea. Previously this only reset local state, so the server (and
		// every viewer) kept the old paragraphs; the next keystroke then inserted
		// fresh paragraphs on top, leaving viewers with old+new concatenated.
		// Delete each existing paragraph (broadcast DELETE ops → server → viewers)
		// so the transcript actually resets everywhere before local state clears.
		// Delete everything the server holds for this document — the local lines
		// and anything the document mirror still has — in ONE message, and drop
		// a pending streaming frame that would re-insert a paragraph afterwards.
		const ids = new Set(lines.map((line) => line.id))
		for (const paragraph of writerDocument?.paragraphs ?? []) ids.add(paragraph.id)
		cancelPendingStream()
		deleteParagraphs([...ids])
		setLines([])
		setCurrentLineId(null)
		if (textareaRef.current) {
			textareaRef.current.value = ''
		}
	}, [lines, writerDocument, deleteParagraphs, cancelPendingStream])

	// After a page reload the server still holds this document, and every viewer
	// still shows it; put it back into the editor with the same paragraph ids so
	// the transcriber continues where they were instead of facing an empty field
	// (MU, Z. Cincibus: "one reload and the whole transcript is lost"). Only
	// into an EMPTY editor, once per document: a reconnect mid-session must not
	// overwrite what is being typed.
	useEffect(() => {
		if (!isRegistered || !documentId || hydratedDocRef.current === documentId) return
		// Only this document's own server copy, never a mirror left from another.
		if (writerDocument?.id !== documentId) return
		const restored = writerLinesFromDocument(writerDocument)
		if (restored.length === 0) return
		hydratedDocRef.current = documentId
		if (lines.length > 0 || textareaRef.current?.value) return
		setLines(restored)
		setCurrentLineId(restored[restored.length - 1]?.id ?? null)
		if (textareaRef.current) {
			textareaRef.current.value = restored.map((line) => line.text).join('\n')
		}
	}, [isRegistered, documentId, writerDocument, lines.length])

	// Focus textarea when registered
	useEffect(() => {
		if (isRegistered && textareaRef.current) {
			textareaRef.current.focus()
		}
	}, [isRegistered])

	if (roleLoading || (!!writerRoomId && roomLoading)) {
		return (
			<div className="flex justify-center py-12 text-primary">
				<Spinner size="lg" />
			</div>
		)
	}

	// Server `liveSessions.ensure` allows global presenters OR the manager of the
	// target room (space owner / space-admin). Mirror that here so an authorised
	// space owner isn't shown the access-denied card after starting a session
	// from their own room, while unauthorised users still see it immediately.
	if (!canWrite) {
		return (
			<div className="max-w-2xl mx-auto space-y-4 px-4 py-8 animate-enter">
				<Link
					to="/"
					className={cn(
						buttonClasses({ variant: 'ghost', size: 'sm' }),
						'inline-flex items-center gap-2 w-fit',
					)}
				>
					<ArrowLeft size={18} />
					{t('common.back')}
				</Link>
				<div className="card-refined p-12 text-center">
					<Shield size={48} className="mx-auto mb-4 opacity-30" />
					<p className="text-lg font-medium">{t('sessions.create.presenterRequiredTitle')}</p>
					<p className="text-base-content/60 mt-2">{t('sessions.create.presenterRequiredDesc')}</p>
				</div>
			</div>
		)
	}

	return (
		<div className="space-y-6 animate-enter">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-display font-semibold">Writer</h1>
				<p className="text-base-content/60 mt-1">Stream transcript text to viewers in real-time</p>
			</div>

			{/* Session Setup or Active Session */}
			{!isSessionActive ? (
				<SessionManager
					currentRole="WRITER"
					roomId={searchParams.get('roomId')?.trim() || undefined}
					onSessionReady={handleSessionReady}
				/>
			) : (
				<>
					{/* Connection Status Bar */}
					<div className="card-refined p-4">
						<div className="flex items-center justify-between flex-wrap gap-3">
							<div className="flex items-center gap-4">
								<div className="flex items-center gap-2">
									{isAuthError ? (
										// The socket may technically be open, but registration was
										// rejected — "Registering..." would spin forever here.
										<>
											<WifiOff size={16} className="text-error" />
											<span className="text-sm text-error">{t('writer.sessionExpiredTitle')}</span>
										</>
									) : isConnecting ? (
										<>
											<span className="text-warning">
												<Spinner size="sm" />
											</span>
											<span className="text-sm text-warning">Connecting...</span>
										</>
									) : isConnected && isRegistered ? (
										<>
											<Wifi size={16} className="text-success" />
											<span className="text-sm text-success">Writer registered</span>
										</>
									) : isConnected ? (
										<>
											<span className="text-warning">
												<Spinner size="sm" />
											</span>
											<span className="text-sm text-warning">Registering...</span>
										</>
									) : (
										<>
											<WifiOff size={16} className="text-error" />
											<span className="text-sm text-error">Disconnected</span>
										</>
									)}
								</div>

								<div className="text-sm text-base-content/60">
									<span className="font-mono">{documentId}</span>
								</div>

								{/* MP-194: the viewer count is a toggle that reveals the live
								    roster (who is reading the transcript). The count itself is
								    the real-time WS `subscriberCount`; the names come from the
								    polled roster query. Mirrors the presenter page. */}
								{isConnected && isRegistered && subscriberCount !== null && (
									<div className="relative">
										<button
											type="button"
											onClick={() => setShowViewers((v) => !v)}
											className={cn(
												buttonClasses({ variant: 'ghost', size: 'sm' }),
												'inline-flex items-center gap-1.5',
											)}
											aria-expanded={showViewers}
										>
											<Users size={14} aria-hidden="true" />
											{t('sessions.present.viewers.label', { total: subscriberCount })}
											<ChevronDown
												size={14}
												aria-hidden="true"
												className={cn('transition-transform', showViewers && 'rotate-180')}
											/>
										</button>
										{showViewers && (
											<div className="absolute left-0 z-20 mt-1 w-64 rounded-xl border border-base-300 bg-base-100 p-3 shadow-lg text-left">
												<p className="text-xs font-semibold text-base-content/70 mb-2">
													{t('sessions.present.viewers.rosterTitle')}
												</p>
												{/* The transcriber's audience is transcript readers, so the
												    roster lists only transcriptViewers — matching the
												    transcript-only `subscriberCount` badge above. (The presenter
												    page additionally shows video viewers; the Writer doesn't, to
												    keep the count and the roster consistent.) */}
												{!viewersQuery.data ? (
													<p className="text-sm text-base-content/60">{t('common.loading')}</p>
												) : viewersQuery.data.transcriptViewers.length === 0 ? (
													<p className="text-sm text-base-content/60">
														{t('sessions.present.viewers.none')}
													</p>
												) : (
													<ul className="space-y-1 max-h-60 overflow-y-auto">
														{viewersQuery.data.transcriptViewers.map((viewer) => (
															<li key={viewer.identity} className="text-sm truncate">
																{viewer.name ?? t('sessions.present.viewers.anonymous')}
															</li>
														))}
													</ul>
												)}
											</div>
										)}
									</div>
								)}
							</div>

							<div className="flex items-center gap-2">
								{error && !isTerminalFailure && (
									<IconButton
										variant="ghost"
										size="sm"
										onClick={reconnect}
										aria-label={t('writer.reconnect')}
										className="text-warning"
									>
										<RefreshCw />
									</IconButton>
								)}
								<IconButton
									variant="ghost"
									size="sm"
									onClick={() => setShowQr(!showQr)}
									aria-label="Show QR Code"
								>
									<QrCode />
								</IconButton>
								<Button
									variant="ghost"
									size="sm"
									onClick={handleStopSession}
									leadingIcon={<Square size={14} />}
									className="text-error"
								>
									Stop
								</Button>
							</div>
						</div>

						{error && !isTerminalFailure && (
							<Alert tone="error" className="mt-3 text-sm">
								{error}
							</Alert>
						)}

						{/* Terminal failure: dead end without guidance — a stenographer
						    mid-session needs to know recovery is possible and what
						    survives it. Auth failures additionally offer re-login. */}
						{isTerminalFailure && (
							<Alert tone="error" className="mt-3 text-sm">
								<div className="space-y-3">
									<div>
										<p className="font-medium">
											{isAuthError
												? t('writer.sessionExpiredTitle')
												: t('writer.connectionLostTitle')}
										</p>
										<p className="mt-1">
											{isAuthError
												? t('writer.sessionExpiredBody')
												: t('writer.connectionLostBody')}
										</p>
										{isAuthError && <p className="mt-1">{t('writer.copyBeforeLeaving')}</p>}
									</div>
									<div className="flex flex-wrap items-center gap-2">
										<Button
											variant={isAuthError ? 'ghost' : 'primary'}
											size="sm"
											onClick={reconnect}
											leadingIcon={<RefreshCw size={14} />}
										>
											{t('writer.reconnect')}
										</Button>
										{isAuthError && (
											<Button
												variant="primary"
												size="sm"
												onClick={handleLogInAgain}
												leadingIcon={<LogIn size={14} />}
											>
												{t('writer.logInAgain')}
											</Button>
										)}
									</div>
								</div>
							</Alert>
						)}
					</div>

					{/* Session URL */}
					<div className="card-refined p-4 space-y-3">
						<div className="flex items-center gap-2">
							<label className="text-sm font-medium">Session URL:</label>
							<code className="text-sm bg-base-200 px-2 py-1 rounded flex-1 truncate">
								{sessionViewerUrl}
							</code>
							<IconButton variant="ghost" size="sm" onClick={handleCopyUrl} aria-label="Copy URL">
								{copied ? <Check className="text-success" /> : <Copy />}
							</IconButton>
						</div>

						{showQr && sessionViewerUrl && (
							<div className="flex justify-center py-4">
								<div className="bg-white p-4 rounded-xl">
									<QRCodeSVG value={sessionViewerUrl} size={200} level="M" />
								</div>
							</div>
						)}
					</div>

					{/* Text Input Area */}
					<div className="card-refined p-4 space-y-3">
						<div className="flex items-center justify-between">
							<h2 id="writer-input-heading" className="text-lg font-semibold">
								Transcript Input
							</h2>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleClearText}
								leadingIcon={<Trash2 size={14} />}
								className="text-base-content/50"
							>
								Clear
							</Button>
						</div>

						<textarea
							ref={textareaRef}
							className="w-full min-h-[400px] p-4 rounded-lg border border-base-300 bg-base-100 font-mono text-base leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
							placeholder={
								isRegistered
									? 'Start typing your transcript here...\nEach line becomes a paragraph.\nText streams to viewers in real-time.'
									: 'Waiting for connection...'
							}
							aria-labelledby="writer-input-heading"
							// readOnly, not disabled: a disabled control drops focus to <body>,
							// so every connection blip left a screen reader announcing the page
							// ("Polygraf Online, document") with the caret gone (retest
							// 24. 9. 2026). handleTextareaChange already ignores input while
							// unregistered.
							readOnly={!isRegistered}
							aria-disabled={!isRegistered}
							onChange={handleTextareaChange}
							spellCheck={false}
						/>

						<div className="flex items-center justify-between text-xs text-base-content/50">
							<span>
								Each line = one paragraph. Text streams in real-time to all connected viewers.
							</span>
							{currentLineId && (
								<span className="font-mono">Active: {currentLineId.slice(0, 12)}...</span>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	)
}

export default function WriterPage() {
	return (
		<AuthGuard>
			<WriterContent />
		</AuthGuard>
	)
}
